import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { Alert, Avatar, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { type FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth/auth-store";
import { ParticleSphereBackground } from "@/features/auth/ParticleSphereBackground";

interface LocationState {
  from?: string;
}

/**
 * ログイン画面。`demo` / `password` の固定資格情報のみ受け付ける。
 *
 * 認証成功後は遷移元 (`location.state.from`) に戻る。直接アクセスされた場合は `/`。
 *
 * デザイン: glassmorphism。背景はパーティクル球の Canvas アニメーション
 * (`ParticleSphereBackground`)。半透明 + `backdrop-filter: blur` のカード
 * 越しにパーティクルが透けて見える。
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const ok = login(username.trim(), password);
    if (!ok) {
      setError("ユーザ名またはパスワードが正しくありません");
      return;
    }
    const from = (location.state as LocationState | null)?.from ?? "/";
    navigate(from, { replace: true });
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        overflow: "hidden",
      }}
    >
      <ParticleSphereBackground />

      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 380,
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(18px) saturate(160%)",
          WebkitBackdropFilter: "blur(18px) saturate(160%)",
          border: "1px solid rgba(255, 255, 255, 0.18)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.45)",
        }}
      >
        <Stack spacing={2} sx={{ alignItems: "center", mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.25)",
              border: "1px solid rgba(255, 255, 255, 0.4)",
              color: "#fff",
              width: 52,
              height: 52,
              backdropFilter: "blur(8px)",
            }}
          >
            <LockRoundedIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
            Factory Digital Twin
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
            ログインしてください
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="ユーザ名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              fullWidth
              size="small"
              sx={glassFieldSx}
            />
            <TextField
              label="パスワード"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              fullWidth
              size="small"
              sx={glassFieldSx}
            />
            {error ? (
              <Alert
                severity="error"
                sx={{
                  background: "rgba(230, 57, 70, 0.18)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.25)",
                  backdropFilter: "blur(6px)",
                  "& .MuiAlert-icon": { color: "#fff" },
                }}
              >
                {error}
              </Alert>
            ) : null}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              sx={{
                background: "rgba(255, 255, 255, 0.28)",
                border: "1px solid rgba(255, 255, 255, 0.45)",
                color: "#fff",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.4)",
                },
              }}
            >
              ログイン
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * 半透明の glass 風 TextField スタイル。ラベル・枠線・テキストを白基調にする。
 */
const glassFieldSx = {
  "& .MuiOutlinedInput-root": {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(6px)",
    color: "#fff",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.4)" },
    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.7)" },
    "&.Mui-focused fieldset": { borderColor: "#fff" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.85)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
  "& input:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 1000px rgba(255,255,255,0.08) inset",
    WebkitTextFillColor: "#fff",
    caretColor: "#fff",
    transition: "background-color 9999s ease-in-out 0s",
  },
} as const;
